import { gql } from '@apollo/client';

export const GET_ME = gql`
  query GetMe {
    me {
      id
      username
      email
      role
      isActive
      preferredDashboardCurrency
      company {
        id
        name
        subscriptionEndDate
        isActive
      }
    }
  }
`;

export const SEARCH_CLIENTS = gql`
  query SearchClients($companyId: ID!, $searchTerm: String!) {
    searchClients(companyId: $companyId, searchTerm: $searchTerm) {
      id
      name
      nameEn
      eik
      vatNumber
      address
      phone
      email
      contactPerson
      clientType
      isIndividual
      countryCode
    }
  }
`;

export const GET_CLIENTS = gql`
  query GetClients($companyId: ID!) {
    clientsByCompany(companyId: $companyId) {
      id
      name
      eik
      vatNumber
      address
      phone
      email
      clientType
      isActive
      isEuVatPayer
      website
      paymentTerms
      creditLimit
      discountPercent
    }
  }
`;

export const GET_VIES_COMPANY_DATA = gql`
  query GetViesCompanyData($vatNumber: String!) {
    getViesCompanyData(vatNumber: $vatNumber) {
      vatNumber
      countryCode
      companyName
      address
      requestDate
      isValid
      fromCache
      hasError
      errorMessage
    }
  }
`;

export const GET_BANK_ACCOUNTS_BY_COMPANY = gql`
  query GetBankAccountsByCompany($companyId: ID!) {
    bankAccountsByCompany(companyId: $companyId) {
      id
      bankName
      iban
      bic
      currencyCode
      accountName
      isActive
      isDefault
      sortOrder
      description
      displayName
      formattedIban
      isBgnAccount
      isForeignCurrencyAccount
      isWiseConnected
      wiseLastSync
      hasCertificate
      certificateS3Key
    }
  }
`;

export const GET_ITEMS = gql`
  query GetItems($companyId: ID!) {
    itemsByCompany(companyId: $companyId) {
      id
      name
      description
      unit
      unitPrice
      vatRate {
        id
        name
        rate
      }
    }
  }
`;

export const GET_DOCUMENT_SEQUENCES = gql`
  query GetDocumentSequences($companyId: ID!) {
    documentSequences(companyId: $companyId) {
      id
      sequenceType
      currentNumber
      nextNumber
      lastUpdated
    }
  }
`;
export const GET_ALL_COMPANIES_WITH_DETAILS = gql`
  query GetAllCompaniesWithDetails {
    allCompanies {
      id
      name
      nameEn
      eik
      vatNumber
      address
      phone
      email
      website
      userLimit
      activeUserCount
      adminUsername
      subscriptionEndDate
      isActive
      createdAt
    }
  }
`;

export const GET_CLIENT_BY_ID = gql`
  query GetClientById($id: ID!) {
    client(id: $id) {
      id
      name
      nameEn
      address
      vatNumber
      eik
      email
      phone
      website
      contactPerson
      clientType
      isActive
      isEuVatPayer
      isIndividual
      paymentTerms
      creditLimit
      discountPercent
      notes
      createdAt
      updatedAt
    }
  }
`;

export const GET_CLIENT_DOCUMENTS_COUNT = gql`
  query GetClientDocumentsCount($clientId: ID!) {
    clientDocumentsCount(clientId: $clientId) {
      hasDocuments
      totalDocuments
    }
  }
`;

export const VALIDATE_VAT_NUMBER = gql`
  query ValidateVatNumber($vatNumber: String!) {
    validateVatNumber(vatNumber: $vatNumber) {
      isValid
      vatNumber
      countryCode
      companyName
      address
      requestDate
    }
  }
`;

export const SEARCH_CLIENT_BY_VAT_NUMBER = gql`
  query SearchClientByVatNumber($companyId: ID!, $vatNumber: String!) {
    searchClientByVatNumber(companyId: $companyId, vatNumber: $vatNumber) {
      success
      client {
        id
        name
        eik
        vatNumber
        address
        email
        phone
        clientType
      }
      message
      errors
      fromVies
      requiresManualEntry
      errorMessage
      errorType
      viesData {
        vatNumber
        countryCode
        companyName
        address
        isValid
      }
    }
  }
`;

export const GET_COMPANY_DETAILS = gql`
  query GetCompanyDetails($id: ID!) {
    companyById(id: $id) {
      id
      name
      nameEn
      address
      vatNumber
      eik
      mol
      email
      phone
      website
      isVatRegistered
      taxRegistrationDate
      defaultPaymentTerms
      logoUrl
      companyStampUrl
      signatureUrl
      invoiceFooter
      invoiceFooterEn
      compiledBy
      wiseApiKey
      wiseEnabled
    }
  }
`;

export const GET_NOMENCLATURES = gql`
  query GetNomenclatures {
    allCountries {
      code
      name
      nameEn
      isEuMember
    }
    allUnitsOfMeasure {
      code
      name
      nameEn
      symbol
      category
    }
  }
`;

export const GET_PAYMENT_METHODS_BY_COMPANY = gql`
  query GetPaymentMethodsByCompany($companyId: ID!) {
    paymentMethodsByCompany(companyId: $companyId) {
      id
      name
      nameEn
      methodCode
      isActive
      isDefault
      requiresBankAccount
      sortOrder
      description
    }
  }
`;

export const GET_NEXT_DOCUMENT_NUMBER = gql`
  query GetNextDocumentNumber($companyId: ID!, $documentType: DocumentType!) {
    nextDocumentNumber(companyId: $companyId, documentType: $documentType)
  }
`;

export const GET_ACTIVE_VAT_RATES = gql`
  query GetActiveVatRates {
    activeVatRates {
      id
      rateValue
      rateName
      rateNameEn
      isDefault
      isActive
      description
      sortOrder
      isZeroRate
      formattedRate
    }
  }
`;

export const GET_LATEST_EXCHANGE_RATES = gql`
  query GetLatestExchangeRates {
    latestExchangeRates {
      id
      currency {
        code
        name
        symbol
      }
      rateDate
      rate
      baseCurrency
    }
  }
`;

export const GET_ACTIVE_ITEMS_BY_COMPANY = gql`
  query GetActiveItemsByCompany($companyId: ID!) {
    activeItemsByCompany(companyId: $companyId) {
      id
      itemNumber
      name
      nameEn
      description
      unitOfMeasure
      unitPrice
      defaultVatRate
      accountingAccountNumber
      isActive
    }
  }
`;

export const GET_ALL_CURRENCIES = gql`
  query GetAllCurrencies {
    allCurrencies {
      code
      name
      symbol
      isActive
    }
  }
`;

export const GET_ACTIVE_CURRENCIES = gql`
  query GetActiveCurrencies {
    activeCurrencies {
      code
      name
      symbol
      isActive
    }
  }
`;

export const GET_ITEMS_BY_COMPANY = gql`
  query GetItemsByCompany($companyId: ID!) {
    itemsByCompany(companyId: $companyId) {
      id
      itemNumber
      name
      nameEn
      description
      unitOfMeasure
      unitPrice
      defaultVatRate
      accountingAccountNumber
      isActive
    }
  }
`;

export const SEARCH_ITEMS = gql`
  query SearchItems($searchTerm: String!, $companyId: ID!) {
    searchItems(searchTerm: $searchTerm, companyId: $companyId) {
      id
      itemNumber
      name
      nameEn
      description
      unitOfMeasure
      unitPrice
      defaultVatRate
      accountingAccountNumber
      isActive
      company {
        id
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_ALL_VAT_EXEMPTION_REASONS = gql`
  query GetAllVatExemptionReasons {
    allVatExemptionReasons {
      id
      reasonCode
      reasonName
      reasonNameEn
      legalBasis
      legalBasisEn
      description
      isActive
      sortOrder
      ublCategoryCode
      ublExemptionCode
    }
  }
`;

export const GET_CURRENCY_SYSTEM_STATUS = gql`
  query GetCurrencySystemStatus {
    currencySystemStatus {
      eurozoneActive
      transitionDate
      activeProvider
      baseCurrency
      defaultCurrency
      bgnToEurRate
      forceEurozoneMode
    }
  }
`;
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($companyId: ID!) {
    dashboardStats(companyId: $companyId) {
      totalClients
      totalInvoices
      totalRevenue
      pendingInvoices
      overduedInvoices
      recentInvoices {
        id
        documentNumber
        clientName
        totalAmountWithVat
        currency
        issueDate
        dueDate
        status
      }
      monthlyRevenue {
        month
        revenue
      }
    }
  }
`;

export const GET_CLIENTS_BY_COMPANY = gql`
  query GetClientsByCompany($companyId: ID!) {
    clientsByCompany(companyId: $companyId) {
      id
      name
      nameEn
      eik
      vatNumber
      address
      email
      phone
      clientType
      isActive
      isEuVatPayer
      isIndividual
      paymentTerms
      creditLimit
      discountPercent
      notes
      createdAt
      updatedAt
    }
  }
`;

export const GET_DOCUMENTS_BY_COMPANY = gql`
  query GetDocumentsByCompany($companyId: ID!) {
    documentsByCompany(companyId: $companyId) {
      id
      documentNumber
      documentType
      issueDate
      dueDate
      totalAmountWithVat
      currencyCode
      status
      isPaid
      clientId
      clientName
      notes
      createdAt
      updatedAt
    }
  }
`;

export const GET_DOCUMENTS_BY_COMPANY_PAGED = gql`
  query GetDocumentsByCompanyPaged($companyId: ID!, $page: Int, $size: Int, $search: String, $dateFrom: String, $dateTo: String) {
    documentsByCompanyPaged(companyId: $companyId, page: $page, size: $size, search: $search, dateFrom: $dateFrom, dateTo: $dateTo) {
      content {
        id
        documentNumber
        documentType
        issueDate
        dueDate
        totalAmountWithVat
        currencyCode
        status
        isPaid
        clientId
        clientName
        notes
        createdAt
        updatedAt
      }
      totalElements
      totalPages
      page
      size
    }
  }
`;
export const GET_DOCUMENT_BY_ID = gql`
  query GetDocumentById($id: ID!) {
    documentById(id: $id) {
      id
      documentUuid
      documentNumber
      fullDocumentNumber
      documentType
      status
      issueDate
      vatDate
      dueDate
      totalAmountWithVat
      subtotalAmount
      vatAmount
      currencyCode
      exchangeRate
      exchangeRateDate
      totalAmountWithVatBaseCurrency
      isTaxDocument
      isCancelled
      cancelledAt
      cancellationReason
      hasValidVatDate
      effectiveVatDate
      createdAt
      updatedAt
      client {
        id
        name
        address
        vatNumber
        eik
        email
        contactPerson
      }
      company {
        id
        name
        address
        vatNumber
        eik
        mol
        compiledBy
      }
      paymentMethod {
        id
        name
      }
      bankAccount {
        id
        bankName
        iban
        bic
      }
      notes
      documentItems {
        id
        itemDescription
        effectiveItemName
        quantity
        unitPrice
        vatRate
        lineTotal
        vatAmount
        item {
          id
          itemNumber
          name
          unitOfMeasure
        }
        vatExemptionReason {
          id
          reasonName
          legalBasis
        }
      }
    }
  }
`;
export const GET_DOCUMENTS = gql`
  query GetDocuments($companyId: ID!) {
    documentsByCompany(companyId: $companyId) {
      id
      documentNumber
      fullDocumentNumber
      documentType
      status
      issueDate
      dueDate
      totalAmountWithVat
      currencyCode
      isPaid
      paidAt
      paymentMethod {
        id
        methodCode
        name
      }
      client {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
  `;

export const GET_DOCUMENTS_FOR_REPORTS = gql`
  query GetDocumentsForReports($companyId: ID!) {
    documentsByCompany(companyId: $companyId) {
      id
      documentNumber
      fullDocumentNumber
      documentType
      status
      issueDate
      dueDate
      totalAmountWithVat
      currencyCode
      isPaid
      paidAt
      paymentMethod {
        id
        methodCode
        name
      }
      client {
        id
        name
      }
      documentItems {
        id
        quantity
        unitPrice
        lineTotal
        item {
          id
          name
          itemNumber
        }
      }
      createdAt
      updatedAt
    }
  }
  `;
  
  export const GET_ALL_USERS = gql`
    query GetAllUsers {
      allUsers {
        id
        username
        email
        role
        isActive
        createdAt
        company {
          id
          name
        }
      }
    }
  `;
  
  export const GET_USERS_BY_COMPANY = gql`
    query GetUsersByCompany($companyId: ID!) {
      usersByCompany(companyId: $companyId) {
        id
        username
        email
        role
        isActive
        firstName
        lastName
      }
    }
  `;
  
  export const GET_COMPANY_WITH_USERS = gql`
    query GetCompanyWithUsers($id: ID!) {
      companyById(id: $id) {
        id
        name
        nameEn
        eik
        vatNumber
        address
        phone
        email
        website
        userLimit
        users {
          id
          username
          email
          role
          isActive
        }
      }
    }
  `;

export const GET_EXCHANGE_RATES_FOR_DATE = gql`
  query GetExchangeRatesForDate($date: String!) {
    exchangeRatesForDate(date: $date) {
      id
      currency {
        code
        name
        symbol
      }
      rate
      rateDate
      baseCurrency
    }
  }
`;

// SMTP Settings queries
export const GET_SMTP_SETTINGS = gql`
  query GetSmtpSettings {
    allSmtpSettings {
      id
      smtpHost
      smtpPort
      smtpUsername
      useTls
      useSsl
      smtpAuth
      fromEmail
      fromName
      isActive
      providerName
      createdAt
      updatedAt
    }
  }
`;

export const VERIFY_DOCUMENT = gql`
  query VerifyDocument($uuid: String!) {
    verifyDocument(uuid: $uuid) {
      documentUuid
      documentNumber
      documentType
      issueDate
      totalAmountWithVat
      currencyCode
      companyName
      companyEik
      clientName
      isValid
      hasBankCertificate
      bankCertificateBase64
      ublXml
    }
  }
`;

export const GET_ACTIVE_SMTP_SETTINGS = gql`
  query GetActiveSmtpSettings {
    activeSmtpSettings {
      id
      smtpHost
      smtpPort
      smtpUsername
      useTls
      useSsl
      smtpAuth
      fromEmail
      fromName
      isActive
      providerName
      createdAt
      updatedAt
    }
  }
`;

export const GET_EXCHANGE_RATE_FOR_CURRENCY = gql`
  query GetExchangeRateForCurrency($currencyCode: String!, $date: String!) {
    exchangeRateForCurrency(currencyCode: $currencyCode, date: $date) {
      id
      currency {
        code
        name
        symbol
      }
      rateDate
      rate
      baseCurrency
    }
  }
`;

// Backup Settings queries
export const GET_BACKUP_SETTINGS = gql`
  query GetBackupSettings {
    backupSettings {
      id
      s3Endpoint
      s3Region
      s3BucketName
      s3AccessKey
      backupPrefix
      autoBackupEnabled
      backupCronExpression
      retentionDays
      maxBackups
      isActive
      lastBackupAt
      lastBackupStatus
      lastBackupSizeBytes
      lastBackupFilename
      lastErrorMessage
      createdAt
      updatedAt
      hasValidConfiguration
    }
  }
`;

export const GET_BACKUP_HISTORY = gql`
  query GetBackupHistory {
    backupHistory {
      id
      filename
      s3Key
      sizeBytes
      status
      backupType
      startedAt
      completedAt
      durationSeconds
      errorMessage
      databaseName
      checksum
      initiatedBy
      deletedAt
      formattedSize
    }
  }
`;

export const GET_ALL_BACKUP_HISTORY = gql`
  query GetAllBackupHistory {
    allBackupHistory {
      id
      filename
      s3Key
      sizeBytes
      status
      backupType
      startedAt
      completedAt
      durationSeconds
      errorMessage
      databaseName
      checksum
      initiatedBy
      deletedAt
      formattedSize
    }
  }
`;

export const GET_BACKUP_STATS = gql`
  query GetBackupStats {
    backupStats {
      totalBackups
      totalSizeBytes
      formattedTotalSize
      lastBackupAt
      lastBackupStatus
      isScheduled
      nextBackupTime
    }
  }
`;

// UBL Export query
export const EXPORT_DOCUMENT_AS_UBL = gql`
  query ExportDocumentAsUbl($documentId: ID!) {
    exportDocumentAsUbl(documentId: $documentId) {
      success
      xml
      filename
      message
      validationErrors
    }
  }
`;

export const GENERATE_DOCUMENT_PDF = gql`
  query GenerateDocumentPdf($documentId: ID!, $isCopy: Boolean, $printMode: String) {
    generateDocumentPdf(documentId: $documentId, isCopy: $isCopy, printMode: $printMode) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

export const GENERATE_DOCUMENT_ODT = gql`
  query GenerateDocumentOdt($documentId: ID!) {
    generateDocumentOdt(documentId: $documentId) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

export const GENERATE_DOCUMENT_XLSX = gql`
  query GenerateDocumentXlsx($documentId: ID!) {
    generateDocumentXlsx(documentId: $documentId) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

export const GENERATE_DOCUMENT_ODS = gql`
  query GenerateDocumentOds($documentId: ID!) {
    generateDocumentOds(documentId: $documentId) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

// Company Reports - Documents
export const GENERATE_DOCUMENTS_REPORT_XLSX = gql`
  query GenerateDocumentsReportXlsx($companyId: ID!, $dateFrom: String, $dateTo: String) {
    generateDocumentsReportXlsx(companyId: $companyId, dateFrom: $dateFrom, dateTo: $dateTo) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

export const GENERATE_DOCUMENTS_REPORT_ODS = gql`
  query GenerateDocumentsReportOds($companyId: ID!, $dateFrom: String, $dateTo: String) {
    generateDocumentsReportOds(companyId: $companyId, dateFrom: $dateFrom, dateTo: $dateTo) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

// Company Reports - Items
export const GENERATE_ITEMS_REPORT_XLSX = gql`
  query GenerateItemsReportXlsx($companyId: ID!, $dateFrom: String, $dateTo: String) {
    generateItemsReportXlsx(companyId: $companyId, dateFrom: $dateFrom, dateTo: $dateTo) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

export const GENERATE_ITEMS_REPORT_ODS = gql`
  query GenerateItemsReportOds($companyId: ID!, $dateFrom: String, $dateTo: String) {
    generateItemsReportOds(companyId: $companyId, dateFrom: $dateFrom, dateTo: $dateTo) {
      success
      pdfBase64
      filename
      errorMessage
    }
  }
`;

// Audit Log queries
export const GET_AUDIT_LOGS_BY_DOCUMENT = gql`
  query GetAuditLogsByDocument($documentId: ID!) {
    auditLogsByDocument(documentId: $documentId) {
      id
      action
      entityType
      entityId
      userId
      username
      companyId
      companyName
      timestamp
      ipAddress
      description
      success
      errorMessage
    }
  }
`;

export const GET_LOGIN_ACTIVITY_LOGS = gql`
  query GetLoginActivityLogs($page: Int, $size: Int, $search: String, $status: String) {
    loginActivityLogs(page: $page, size: $size, search: $search, status: $status) {
      content {
        id
        action
        username
        companyName
        timestamp
        ipAddress
        userAgent
        description
        success
        errorMessage
      }
      totalElements
      totalPages
      page
      size
    }
  }
`;

// ========== GLOBAL CURRENCIES (MongoDB - SUPER_ADMIN) ==========

export const GET_ALL_GLOBAL_CURRENCIES = gql`
  query GetAllGlobalCurrencies {
    allGlobalCurrencies {
      code
      nameEn
      nameBg
      symbol
      decimalPlaces
      activeInEcb
      enabledGlobally
      country
      sortOrder
      createdAt
      updatedAt
      lastSyncAt
      notes
    }
  }
`;

export const GET_ENABLED_GLOBAL_CURRENCIES = gql`
  query GetEnabledGlobalCurrencies {
    enabledGlobalCurrencies {
      code
      nameEn
      nameBg
      symbol
      decimalPlaces
      activeInEcb
      enabledGlobally
      sortOrder
    }
  }
`;

export const GET_GLOBAL_CURRENCY_STATS = gql`
  query GetGlobalCurrencyStats {
    globalCurrencyStats {
      totalCurrencies
      enabledCurrencies
      totalRates
      latestRateDate
      oldestRateDate
    }
  }
`;

export const GET_GLOBAL_RATES_FOR_DATE = gql`
  query GetGlobalRatesForDate($date: String!) {
    globalRatesForDate(date: $date) {
      id
      currencyCode
      rateDate
      rate
      baseCurrency
      source
      syncedAt
    }
  }
`;

export const GET_GLOBAL_RATES_FOR_PERIOD = gql`
  query GetGlobalRatesForPeriod($fromDate: String!, $toDate: String!) {
    globalRatesForPeriod(fromDate: $fromDate, toDate: $toDate) {
      id
      currencyCode
      rateDate
      rate
      baseCurrency
      source
      syncedAt
    }
  }
`;

export const GET_LATEST_GLOBAL_RATES = gql`
  query GetLatestGlobalRates {
    latestGlobalRates {
      id
      currencyCode
      rateDate
      rate
      baseCurrency
      source
      syncedAt
    }
  }
`;

export const GET_SYSTEM_USAGE_STATS = gql`
  query GetSystemUsageStats {
    getSystemUsageStats {
      companyId
      companyName
      emailCount24h
      emailCount30d
      invoiceCount24h
      invoiceCount30d
      totalDocuments
      totalUsers
    }
  }
`;

export const GET_SITE_SETTINGS = gql`
  query GetSiteSettings {
    siteSettings {
      id
      siteName
      siteDomain
      termsUrl
      blogUrl
      shopUrl
      serviceDescription
      servicePrice
      priceCurrency
      showBuyButton
      showTermsLink
      showBlogLink
      createdAt
      updatedAt
    }
  }
`;

export const GET_PUBLIC_SITE_SETTINGS = gql`
  query GetPublicSiteSettings {
    publicSiteSettings {
      id
      siteName
      siteDomain
      termsUrl
      blogUrl
      shopUrl
      serviceDescription
      servicePrice
      priceCurrency
      showBuyButton
      showTermsLink
      showBlogLink
    }
  }
`;

// Dashboard currency chart queries
export const GET_CURRENCY_RATE_HISTORY = gql`
  query GetCurrencyRateHistory($currencyCode: String!, $days: Int!) {
    currencyRateHistory(currencyCode: $currencyCode, days: $days) {
      date
      rate
    }
  }
`;

export const GET_ENABLED_CURRENCIES = gql`
  query GetEnabledCurrencies {
    enabledGlobalCurrencies {
      code
      nameEn
      nameBg
      symbol
    }
  }
`;